import { SurveyModel, Question, Event, Serializer, EventBase, surveyLocalization } from "survey-core";
import { hasLicense } from "survey-core";
import {
  IPermission,
  QuestionLocation,
  ITableState,
  IColumn,
  IColumnData,
} from "./config";
import { Details } from "./extensions/detailsextensions";
import { localization } from "../localizationManager";
import { ITableExtension, TableExtensions } from "./extensions/tableextensions";
import { createCommercialLicenseLink } from "../utils";
import { ColumnsBuilderFactory } from "./columnbuilder";
import { DefaultColumn } from "./columns";
import { ITableOptions, ITable } from "./table-interfaces";

export type TabulatorFilter = { field: string, type: string, value: any };
export type TabulatorSortOrder = { field: string, direction: undefined | "asc" | "desc" };
export type GetDataUsingCallbackFn = (params: { filter?: Array<TabulatorFilter>, sort?: Array<TabulatorSortOrder>, offset?: number, limit?: number, callback?: (response: { data: Array<Object>, totalCount: number, error?: any }) => void }) => void;
export type GetDataUsingPromiseFn = (params: { filter?: Array<TabulatorFilter>, sort?: Array<TabulatorSortOrder>, offset?: number, limit?: number }) => Promise<{ data: Array<Object>, totalCount: number, error?: any }>;
export type GetDataFn = GetDataUsingCallbackFn | GetDataUsingPromiseFn;
// export type GetDataFn = (params: { filter?: any, limit?: number, offset?: number, callback?: (response: { data: Array<Object>, total: number, error?: any }) => void }) => Promise<{ data: Array<Object>, total: number, error?: any }> | void;

export class TableEvent extends EventBase<Table> {}

export abstract class Table implements ITable {
  public static showFilesAsImages = false;
  public getShowFilesAsImages(): boolean {
    return Table.showFilesAsImages;
  }
  public static haveCommercialLicense: boolean = false;
  protected tableData: any;
  protected extensions: TableExtensions;
  private haveCommercialLicense = false;
  protected _columns: Array<IColumn>;
  private _columnsByName: Map<string, IColumn> | null = null;
  constructor(
    protected _survey: SurveyModel,
    protected data: Array<Object> | GetDataFn,
    protected _options: ITableOptions = {},
    protected _columnsData: Array<IColumnData> = []
  ) {
    if(!this._options) {
      this._options = {};
    }
    if(typeof this._options.useNestedTables === "undefined") {
      this._options.useNestedTables = true;
    }
    if(typeof this._options.matrixDropdownDetailRowThreshold === "undefined") {
      this._options.matrixDropdownDetailRowThreshold = 5;
    }

    this.initialize();

    this.extensions = new TableExtensions(this);
    const f = hasLicense;
    this.haveCommercialLicense = (!!f && f(4)) ||
      Table.haveCommercialLicense ||
      (!!_options &&
        (typeof _options.haveCommercialLicense !== "undefined"
          ? _options.haveCommercialLicense
          : false));
  }
  protected renderResult: HTMLElement;
  protected currentPageSize: number = 5;
  protected currentPageNumber: number;
  protected _rows: TableRow[] = [];
  protected isColumnReorderEnabled: boolean;

  public isInitialized = false;
  protected initialize(): void {
    this.isInitialized = false;
    this.currentPageSize = this.options.pageSize || 5;
    this.currentPageNumber = 1;
    this.setColumns(this.buildColumns(this._survey));
    this.initTableData(this.data);
    localization.currentLocale = this._survey.locale;

    if(this._columnsData.length !== 0) {
      this.updateColumnsFromData(this._columnsData);
    }
    this.isInitialized = true;
  }

  public getTableData(): Array<any> {
    return [].concat(this.tableData || []);
  }
  /**
   * Sets pagination selector content.
   */
  public paginationSizeSelector: number[] = [1, 5, 10, 25, 50, 100];

  public onColumnsVisibilityChanged = new TableEvent();

  public onColumnsLocationChanged = new TableEvent();

  public onRowRemoved = new TableEvent();

  public renderDetailActions: (
    container: HTMLElement,
    row: TableRow
  ) => HTMLElement;

  public getData() {
    return this.data;
  }
  public get survey() {
    return this._survey;
  }
  public get options() {
    return this._options;
  }

  public abstract applyFilter(value: string): void;
  public abstract applyColumnFilter(columnName: string, value: string): void;
  public abstract sortByColumn(columnName: string, direction: string): void;

  public render(targetNode: HTMLElement | string): void {
    if(typeof targetNode === "string") {
      targetNode = document.getElementById(targetNode);
    }
    targetNode.innerHTML = "";
    if(!this.haveCommercialLicense) {
      targetNode.appendChild(createCommercialLicenseLink());
    }
  }

  public enableColumnReorder() {
    this.isColumnReorderEnabled = true;
  }

  public disableColumnReorder(): void {
    this.isColumnReorderEnabled = false;
  }

  public getPageNumber(): number {
    return this.currentPageNumber;
  }

  public setPageNumber(value: number) {
    this.currentPageNumber = value;
  }

  /**
   * Returns current page size.
   */
  public getPageSize(): number {
    return this.currentPageSize;
  }

  /**
   * Sets current page size.
   */
  public setPageSize(value: number): void {
    this.currentPageSize = value;
    this.stateChanged();
  }

  public getCreatedRows(): TableRow[] {
    return [].concat(this._rows);
  }

  public clearCreatedRows(): void {
    this._rows.forEach((row) => {
      row.destroy();
    });
    this._rows = [];
  }

  public get useNamesAsTitles() {
    return this._options && this._options.useNamesAsTitles === true;
  }

  public get itemsDelimiter(): string {
    return this._options && this._options.itemsDelimiter || ", ";
  }

  protected buildColumns = (survey: SurveyModel) => {
    let columns: Array<IColumn> = [];
    this._survey.getAllQuestions().forEach((question: Question) => {
      if(!this.isNonValueQuestion(question)) {
        const builder = ColumnsBuilderFactory.Instance.getColumnsBuilder(question.getTemplate());
        columns = columns.concat(builder.buildColumns(question, this));
      }
    });
    return columns;
  };
  private isNonValueQuestion(question: Question): boolean {
    return Serializer.isDescendantOf(question.getType(), "nonvalue");
  }

  public isColumnVisible(column: IColumn) {
    if(column.location !== QuestionLocation.Column) return false;
    return column.isVisible;
  }

  public get columns() {
    return [].concat(this._columns);
  }

  public set columns(columns: Array<IColumn>) {
    this.setColumns(columns);
    this.refresh(true);
    this.stateChanged();
  }

  protected setColumns(columns: Array<IColumn>): void {
    this._columns = columns;
    this.invalidateColumnsByNameCache();
  }

  protected invalidateColumnsByNameCache(): void {
    this._columnsByName = null;
  }

  private isInitTableDataProcessingValue: boolean;
  public get isInitTableDataProcessing(): boolean { return this.isInitTableDataProcessingValue; }
  protected initTableData(data: Object[] | GetDataFn): void {
    if(!Array.isArray(data)) {
      this.tableData = undefined;
      return;
    }
    this.isInitTableDataProcessingValue = true;
    this.tableData = (data || []).map((item) => this.processLoadedDataItem(item));
    this.isInitTableDataProcessingValue = false;
  }
  protected processLoadedDataItem(item: any): any {
    var dataItem: any = {};
    // clear previous row values: assigning survey.data merges, which leaks values (e.g. nested questions) into rows that lack them
    this._survey.setDataCore(item, true);
    // setDataCore does not refresh nested questions (e.g. inside choice items), update them explicitly
    const questions = this._survey.getAllQuestions();
    this._survey.getAllQuestions(false, false, true).forEach((question) => {
      // skip questions whose values are owned by a parent question (e.g. inside composite questions)
      if(questions.indexOf(question) < 0 && (<any>question).data === this._survey) {
        question.updateValueFromSurvey(this._survey.getValue(question.getValueName()), true);
      }
    });
    this._columns.forEach((column) => {
      const opt = column.getCellData(this, item);
      if(typeof this._options.onGetQuestionValue === "function") {
        this._options.onGetQuestionValue(opt);
      }
      dataItem[column.name] = opt.displayValue;
    });
    return dataItem;
  }

  public moveColumn(from: number, to: number) {
    var deletedColumns = this._columns.splice(from, 1);
    this._columns.splice(to, 0, deletedColumns[0]);
    this.stateChanged();
  }

  public setColumnLocation(columnName: string, location: QuestionLocation) {
    this.getColumnByName(columnName).location = location;
    this.onColumnsLocationChanged.fire(this, {
      columnName: columnName,
      location: location,
    });
    this.stateChanged();
  }

  public getColumnByName(columnName: string): IColumn {
    if(!this._columnsByName) {
      this._columnsByName = new Map<string, IColumn>();
      for(const column of this._columns) {
        this._columnsByName.set(column.name, column);
      }
    }
    return this._columnsByName.get(columnName);
  }

  public setColumnVisibility(columnName: string, isVisible: boolean) {
    var column = this.getColumnByName(columnName);
    column.isVisible = isVisible;
    this.onColumnsVisibilityChanged.fire(this, {
      columnName: columnName,
      columnVisibility: isVisible,
    });
    this.stateChanged();
  }

  public hideAllColumns(): void {
    this.setAllColumnsVisibility(false);
  }

  public showAllColumns(): void {
    this.setAllColumnsVisibility(true);
  }

  protected setAllColumnsVisibility(isVisible: boolean): void {
    this.lockStateChanged();
    try {
      this._columns.forEach((column) => {
        if(column.isVisible !== isVisible) {
          this.setColumnVisibility(column.name, isVisible);
        }
      });
    } finally {
      this.unlockStateChanged();
    }
    this.stateChanged();
  }

  public setColumnWidth(columnName: string, width: string | number) {
    var column = this.getColumnByName(columnName);
    column.width = width;
    this.stateChanged();
  }

  public removeRow(row: TableRow): void {
    var index = this._rows.indexOf(row);
    this._rows.splice(index, 1);
    this.onRowRemoved.fire(this, { row: row });
  }

  /**
   * Returns current locale of the table.
   * If locales more than one, the language selection dropdown will be added in the toolbar
   */
  public get locale() {
    return localization.currentLocale;
  }

  /**
   * Sets locale for table.
   */
  public set locale(newLocale: string) {
    this._survey.locale = newLocale;
    localization.currentLocale = newLocale;
    this.columns.forEach(c => c.displayName = undefined);
    this.refresh(true);
    this.stateChanged();
  }

  private _lockStateChanged: boolean = false;
  public lockStateChanged() {
    this._lockStateChanged = true;
  }
  public unlockStateChanged() {
    this._lockStateChanged = false;
  }

  protected stateChanged() {
    if(this._lockStateChanged) return;
    this.onStateChanged.fire(this, this.state);
  }

  public getLocales(): Array<string> {
    return [].concat(this._survey.getUsedLocales());
  }

  protected supportSoftRefresh() {
    return false;
  }

  protected softRefresh() {
    this.hardRefresh();
  }

  protected hardRefresh() {
    this.initTableData(this.data);
    if(this.isRendered) {
      this.currentPageNumber = this.getPageNumber();
      const targetNode = this.renderResult;
      this.destroy();
      this.render(targetNode);
      this.setPageSize(this.currentPageSize);
      this.setPageNumber(this.currentPageNumber);
    }
  }

  public refresh(hard: boolean = false) {
    if(hard || !this.supportSoftRefresh()) {
      this.hardRefresh();
    } else {
      this.softRefresh();
    }
  }

  public destroy() {
    this.clearCreatedRows();
    this.extensions.destroy();
    this.renderResult.innerHTML = "";
    this.renderResult = undefined;
  }

  public get isRendered() {
    return !!this.renderResult;
  }

  /**
   * Table state getter.
   */
  public get state(): ITableState {
    return {
      locale: localization.currentLocale,
      elements: JSON.parse(JSON.stringify(this._columns)),
      pageSize: this.currentPageSize,
    };
  }
  /**
   * Table state setter.
   */
  public set state(newState: ITableState) {
    if(!newState) return;

    if(typeof newState.elements !== "undefined") {
      this.updateColumnsFromData(newState.elements);
    }

    if(typeof newState.locale !== "undefined") {
      localization.currentLocale = newState.locale;
      this._survey.locale = newState.locale;
      this.initTableData(this.data);
    }

    if(typeof newState.pageSize !== "undefined") {
      this.currentPageSize = newState.pageSize;
    }

    this.refresh(true);
  }
  /**
   * Resets table state.
   * @since 2.3.5
   */
  public resetState(): void {
    this._survey.locale = surveyLocalization.defaultLocale;
    this.initialize();
    this.refresh(true);
    this.stateChanged();
  }

  private updateColumnsFromData(columnsData: Array<IColumnData>) {
    const columns = this._columns;
    columns.forEach(column => {
      const el = columnsData.filter(el => column.name === el.name)[0];
      if(!!el) {
        column.fromJSON(el);
        column.visibleIndex = columnsData.indexOf(el);
      }
    });
    columnsData.forEach(el => {
      let column = columns.filter(column => column.name === el.name)[0];
      if(!column) {
        column = new DefaultColumn(undefined, this);
        column.fromJSON(el);
        column.visibleIndex = columnsData.indexOf(el);
        columns.push(column);
      }
    });
    this.setColumns(this._columns.sort((col1, col2) => col1.visibleIndex - col2.visibleIndex));
  }

  /**
   * Fires when table state changed.
   */
  public onStateChanged = new TableEvent();

  /**
   * Gets table permissions.
   */
  public get permissions(): IPermission[] {
    return <any>this._columns.map((column: IColumn) => {
      return {
        name: column.name,
        isPublic: column.isPublic,
      };
    });
  }
  /**
   * Sets table permissions.
   */
  public set permissions(permissions: IPermission[]) {
    const updatedElements = this._columns.map((column: IColumn) => {
      permissions.forEach((permission) => {
        if(permission.name === column.name)
          column.isPublic = permission.isPublic;
      });

      return column;
    });
    this.setColumns([].concat(updatedElements));
    this.onPermissionsChangedCallback &&
      this.onPermissionsChangedCallback(this);
  }

  /**
   * Fires when permissions changed
   */
  public onPermissionsChangedCallback: any;

  protected get allowSorting(): boolean {
    return this.options.allowSorting === undefined || this.options.allowSorting === true;
  }

  public allowExtension(extension: ITableExtension): boolean {
    if(extension.location === "column" && extension.name === "sort") {
      return this.allowSorting;
    }
    return true;
  }
}

export abstract class TableRow {
  constructor(
    protected table: Table,
    protected extensionsContainer: HTMLElement,
    protected detailsContainer: HTMLElement
  ) {
    this.details = new Details(table, this, detailsContainer);
    this.extensions = new TableExtensions(table);
    table.onColumnsLocationChanged.add(this.onColumnLocationChangedCallback);
  }
  public details: Details;
  public extensions: TableExtensions;
  private detailedRowClass = "sa-table__detail-row";
  private isDetailsExpanded = false;
  public onToggleDetails: Event<
    (sender: TableRow, options: any) => any,
    TableRow,
    any
  > = new Event<(sender: TableRow, options: any) => any, TableRow, any>();

  /**
   * Returns row's html element
   */
  public abstract getElement(): HTMLElement;

  /**
   * Returns data, which is displayed in the row.
   */
  public abstract getRowData(): any;

  /**
   * Returns position of row in the table's data.
   */
  public abstract getDataPosition(): number;

  protected isSelected: boolean;

  public render() {
    this.extensions.render(this.extensionsContainer, "row", { row: this });
  }

  public openDetails() {
    this.details.open();
    this.getElement().className += " " + this.detailedRowClass;
    this.onToggleDetails.fire(this, { isExpanded: true });
    this.isDetailsExpanded = true;
  }

  public closeDetails() {
    this.details.close();
    this.getElement().classList.remove(this.detailedRowClass);
    this.onToggleDetails.fire(this, { isExpanded: false });
    this.isDetailsExpanded = false;
  }

  public toggleDetails() {
    if(this.isDetailsExpanded) {
      this.closeDetails();
    } else this.openDetails();
  }

  public getIsSelected(): boolean {
    return this.isSelected;
  }

  public toggleSelect(): void {
    this.isSelected = !this.isSelected;
  }

  public remove(): void {
    this.table.removeRow(this);
    this.destroy();
  }

  private onColumnLocationChangedCallback = () => {
    this.closeDetails();
  };

  public destroy(): void {
    this.table.onColumnsLocationChanged.remove(
      this.onColumnLocationChangedCallback
    );
    this.extensions.destroy();
  }
}